import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import TimeList from "@/user/components/fair/time_list";
import Calendar from "@/user/components/fair/calendar";
import ModalButton from "@/user/components/fair/reserve_button";
import { DateTime } from "luxon";
import Axios from "axios";
import { setColorClass, sliderSetting } from "@/shared/calendar";
// eslint-disable-next-line import/no-unresolved
import { ClipLoader } from "react-spinners";
import route from "@/shared/ziggy/route";

// 予約可能な日をすぎた場合
const isNotReservableDay = (date, webReservePeriodDate) => {
  const reserveDate = DateTime.fromISO(date);
  const toDay = DateTime.now().toFormat("yyyy-MM-dd");
  let reservableDay = DateTime.fromFormat(toDay, "yyyy-MM-dd");

  if (webReservePeriodDate > 0) {
    reservableDay = reservableDay.plus({ days: webReservePeriodDate });
  }

  return reserveDate < reservableDay;
};

// 日付ごとの満席チェック
const isFullReserveReminder = (fairDays) => {
  return fairDays.times.every((time) => time.remainder === 2);
};

/**
 * APIアクセス
 * @param {array} $params
 * @returns {json}
 */
const api = ($params) => {
  const endpoint = route("api.user.fair.dateTime");

  return Axios.get(endpoint, {
    params: $params,
  });
};

/**
 *フェアAPIの実行
 * @param {number} fairId 指定されたフェアID
 * @param {number} fairsTotal 該当するフェアの日付データ
 */
const handleFairAPIRequest = (fairId, setFairDays) => {
  return api({ fair_id: fairId }).then(({ data }) => {
    setFairDays(data.fairDate);
    return data.fairDate;
  });
};

function ModalStatus({ startOfMonth, webReservePeriodDate, holidays }) {
  // モーダルの表示非表示
  const [isModalOpen, setIsModalOpen] = useState(false);
  // カレンダーで選択された日付
  const [selectedCalenderDate, setSelectedCalenderDate] = useState("");
  // 日付ごとのフェアデータ
  const [fairDays, setFairDays] = useState({});
  // カレンダー初期日付（日付順）
  const [initializeDate, setInitializeDate] = useState("");
  // モーダルのボタンの表示非表示
  const [isShowModalBtn, setIsShowModalBtn] = useState(false);
  const [fairId, setFairId] = useState(0);
  // 予約可能である
  const [canReserve, setCanReserve] = useState(true);
  // 注意文言
  const [noteText, setNoteText] = useState("");
  // ローディングの表示非表示
  const [isLoading, setIsLoading] = useState(true);

  // モーダルボタン用リンク
  const [showLink, setShowLink] = useState("");
  const [reserveLink, setReserveLink] = useState("");

  const handleCloseModal = () => {
    setSelectedCalenderDate("");
    setInitializeDate("");
    setColorClass(); // カレンダー色調整
    setIsModalOpen(false);
    setIsShowModalBtn(false);
    setCanReserve(true);
    setNoteText("");
    setIsLoading(true);
  };

  useEffect(() => {
    if (fairId) {
      // 詳細画面のURLを生成
      setShowLink(
        route("user.fair.show", {
          fairId,
        }),
      );
      // 予約画面のURLを生成
      setReserveLink(
        route("user.reservation.create", {
          fairId,
        }),
      );
    } else {
      setShowLink("");
      setReserveLink("");
    }
  }, [fairId]);

  // フェアごとのチェック
  const isReservableFair = (reservePeriodDate, fairDaysData) => {
    const reservableDays = []; // 予約期限内の日付一覧
    const reminderDays = []; // 残席ありの日付一覧
    let note = "";
    Object.values(fairDaysData).forEach((fairDay) => {
      fairDay.forEach((day) => {
        // 予約期限がすぎてない
        if (!isNotReservableDay(day.date, reservePeriodDate)) {
          reservableDays.push(day);
        }
      });
    });
    // 予約ができるフェアが0の場合
    if (reservableDays.length === 0) {
      note = `※${reservePeriodDate}日以内のご予約は、別の日付をご選択もしくは、会場に直接お問合せ下さい。`;
    }

    // 予約ができるフェアが1件でもある場合は残席をチェックする
    if (reservableDays.length > 0) {
      reservableDays.forEach((day) => {
        if (!isFullReserveReminder(day)) {
          reminderDays.push(day.date);
        }
      });
      // 残席があるフェアが0の場合（すべて満席の場合）
      if (reminderDays.length === 0) {
        note = "※こちらのフェアは全日程満席です。";
      }
    }
    return note;
  };

  useEffect(() => {
    const openModal = document.getElementsByClassName("js-modal_reserve_open");

    const handleOpenModal = (e) => {
      e.preventDefault();
      // checked を外す
      const checkedInputs = document.querySelectorAll(
        "#js-calendar_fair_reserve_modal input[type=checkbox]:checked",
      );
      checkedInputs.forEach((checkedInput) => {
        // eslint-disable-next-line no-param-reassign
        checkedInput.checked = false;
      });
      // カレンダーの色調整
      setColorClass();
      setIsModalOpen(true);
      const cassette = e.currentTarget;
      setFairId(cassette.dataset.fair_id);

      // API実行
      handleFairAPIRequest(cassette.dataset.fair_id, setFairDays).then(
        (fairDates) => {
          const note = isReservableFair(webReservePeriodDate, fairDates);
          if (note) {
            setCanReserve(false);
            setIsShowModalBtn(true);
            setNoteText(note);
          }
          setIsLoading(false);
        },
      );

      const targetDate = cassette.dataset.date;
      if (targetDate && isNotReservableDay(targetDate, webReservePeriodDate)) {
        setCanReserve(false);
        setIsShowModalBtn(true);
        setNoteText(
          `※${webReservePeriodDate}日以内のご予約は、別の日付をご選択もしくは、会場に直接お問合せ下さい。`,
        );
      }
      setInitializeDate(targetDate);
    };

    if (openModal) {
      Array.from(openModal).forEach((cassette) => {
        cassette.addEventListener("click", handleOpenModal);
      });
    }

    return () => {
      if (openModal) {
        Array.from(openModal).forEach((cassette) => {
          cassette.removeEventListener("click", handleOpenModal);
        });
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isLoading) {
      // カレンダーのカルーセル
      sliderSetting(true, false);

      // カレンダーの日付
      const inputDateCheckBox = document.querySelectorAll(
        '#js-calendar_fair_reserve_modal input[name="date[]"]',
      );

      if (inputDateCheckBox) {
        inputDateCheckBox.forEach((checkDay) => {
          checkDay.addEventListener("click", () => {
            setSelectedCalenderDate(checkDay.value);
            setInitializeDate("");
            setIsShowModalBtn(false);
            setCanReserve(true);
          });
        });
      }
    }
  }, [isLoading]);

  return (
    <div
      className={`fair-modal ${isModalOpen ? "is-active" : ""}`}
      id="fair-modal"
    >
      <div className="fair-modal__overlay" onClick={() => handleCloseModal()} />
      <div className="fair-modal__container">
        <div
          className="fair-modal__close color--text"
          onClick={() => handleCloseModal()}
        >
          ×
        </div>
        <div className="fair-modal__content background-color--page_bg">
          <div id="fair-search-reserve" className="fair-search-reserve">
            <div className="fair-search-reserve__head">
              <p className="fair-search-reserve__title color--accent font-family--middle">
                日時を選択してフェアを予約
              </p>
            </div>
            {/* ローディング */}
            {isLoading ? (
              <ClipLoader
                className={"fair-modal__loading border-color--accent"}
                size={40}
                height={80}
                width={80}
                loading={isLoading}
              />
            ) : (
              <>
                {/* カレンダー表示 */}
                <Calendar
                  startOfMonth={startOfMonth}
                  fairDays={fairDays}
                  webReservePeriodDate={webReservePeriodDate}
                  selectedDate={
                    selectedCalenderDate ? [selectedCalenderDate] : []
                  }
                  initializeDate={initializeDate}
                  type={"fair_reserve_modal"}
                  holidays={holidays}
                />
                {!canReserve && (
                  <p className="fair-search-reserve__note color--text font-family--middle">
                    {noteText}
                  </p>
                )}
                {Object.entries(fairDays).map((fairDay, i) => {
                  const date = initializeDate || selectedCalenderDate;

                  // 選択した日付の開催日でない場合は非表示
                  if (fairDay[0] !== date) {
                    return null;
                  }
                  // 選択した日付の時間がない時は非表示
                  if (!fairDay[1][0]) {
                    return null;
                  }
                  // 予約可能な日をすぎた場合
                  if (isNotReservableDay(date, webReservePeriodDate)) {
                    return null;
                  }
                  // 予約できない場合は非表示
                  if (!canReserve) {
                    return null;
                  }
                  const dayTime = fairDay[1][0];
                  return (
                    <div key={i}>
                      <p className="fair-search-reserve__subtitle color--text font-family--middle">
                        時間を選択
                      </p>
                      <TimeList
                        times={dayTime.times}
                        date={date}
                        showLink={showLink}
                        fairId={fairId}
                      />
                    </div>
                  );
                })}
                {isShowModalBtn && (
                  <ModalButton
                    reserveLink={reserveLink}
                    showLink={showLink}
                    canReserve={canReserve}
                  />
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * startOfMonth: カレンダー開始日(YYYY-mm-dd)
 * webReservePeriodDate: オンライン予約の受付期限（日数）
 */
ModalStatus.propTypes = {
  startOfMonth: PropTypes.string.isRequired,
  webReservePeriodDate: PropTypes.string.isRequired,
};

export default ModalStatus;
